'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { logger } from '~/debug/logger'

import { AssistantAttachmentUploadError, type UploadAttachmentParams, uploadAssistantAttachment } from './api'
import {
  ATTACHMENT_MAX_COUNT,
  type AssistantAttachment,
  type MessageAttachment,
  type PendingAttachment,
  isImageAttachment,
  validateAttachmentFile
} from './attachments'

type UseAssistantAttachmentsInput = {
  organizationId: string | undefined
  /** Creates the conversation when there is none yet and returns its id (from `useAssistantStream`). */
  ensureConversation: (organizationId: string) => Promise<string>
  /** Called once a conversation exists for an upload, so the caller can remember the id. */
  onConversationReady?: (conversationId: string) => void
  /** Uploads one file to the conversation; the assistant service's multipart upload when omitted. */
  uploadAttachment?: (params: UploadAttachmentParams) => Promise<AssistantAttachment>
}

/**
 * Pending-attachment list shared by the billing composer panel and the global assistant bar: validates picked
 * files, uploads them to the (created on demand) conversation with progress, and hands the ready ids to the next
 * message. Rejected files stay in the list as error chips so the user sees why, until removed or sent.
 */
export default function useAssistantAttachments(input: UseAssistantAttachmentsInput) {
  const { organizationId, ensureConversation, onConversationReady, uploadAttachment = uploadAssistantAttachment } = input
  const [attachments, setAttachments] = useState<PendingAttachment[]>([])
  const abortControllersRef = useRef(new Map<string, AbortController>())
  const inFlightRef = useRef(new Map<string, Promise<void>>())
  const previewUrlsRef = useRef(new Map<string, string>())
  const lastOrgIdRef = useRef(organizationId)
  // Mirrors `attachments` synchronously so back-to-back calls (drop + paste, or waitForUploads + takeReady
  // in the same turn) see each other's entries before React commits.
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments

  const update = useCallback((localId: string, patch: Partial<PendingAttachment>) => {
    const next = attachmentsRef.current.map((entry) => (entry.localId === localId ? { ...entry, ...patch } : entry))
    attachmentsRef.current = next
    setAttachments(next)
  }, [])

  const upload = useCallback(
    async (entry: PendingAttachment) => {
      if (!organizationId) {
        update(entry.localId, { status: 'error', error: 'uploadFailed' })
        return
      }
      const controller = new AbortController()
      abortControllersRef.current.set(entry.localId, controller)
      try {
        const conversationId = await ensureConversation(organizationId)
        onConversationReady?.(conversationId)
        if (controller.signal.aborted) return
        const attachment = await uploadAttachment({
          organizationId,
          conversationId,
          file: entry.file,
          signal: controller.signal,
          onProgress: (progress) => update(entry.localId, { progress })
        })
        update(entry.localId, { status: 'ready', progress: 100, attachment })
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') return
        logger.warn('Assistant attachment upload failed', error)
        update(entry.localId, {
          status: 'error',
          error: error instanceof AssistantAttachmentUploadError ? error.code : 'uploadFailed'
        })
      } finally {
        abortControllersRef.current.delete(entry.localId)
      }
    },
    [ensureConversation, onConversationReady, organizationId, update, uploadAttachment]
  )

  const replace = useCallback((next: PendingAttachment[]) => {
    attachmentsRef.current = next
    setAttachments(next)
  }, [])

  const addFiles = useCallback(
    (files: Iterable<File>) => {
      const picked = Array.from(files)
      if (picked.length === 0) return
      const current = attachmentsRef.current
      let slots = ATTACHMENT_MAX_COUNT - current.filter((entry) => entry.status !== 'error').length
      const added = picked.map<PendingAttachment>((file) => {
        const localId = crypto.randomUUID()
        const validation = validateAttachmentFile(file)
        if (validation) {
          return { localId, file, status: 'error', progress: 0, error: validation }
        }
        if (slots <= 0) {
          return { localId, file, status: 'error', progress: 0, error: 'tooMany' }
        }
        slots -= 1
        return { localId, file, status: 'uploading', progress: 0 }
      })
      replace([...current, ...added])
      added
        .filter((entry) => entry.status === 'uploading')
        .forEach((entry) => {
          inFlightRef.current.set(
            entry.localId,
            upload(entry).finally(() => inFlightRef.current.delete(entry.localId))
          )
        })
    },
    [replace, upload]
  )

  const remove = useCallback(
    (localId: string) => {
      abortControllersRef.current.get(localId)?.abort()
      abortControllersRef.current.delete(localId)
      replace(attachmentsRef.current.filter((entry) => entry.localId !== localId))
    },
    [replace]
  )

  const clear = useCallback(() => {
    abortControllersRef.current.forEach((controller) => controller.abort())
    abortControllersRef.current.clear()
    replace([])
  }, [replace])

  /** Resolves once no upload is in flight; failures are reflected on the entries, not thrown. */
  const waitForUploads = useCallback(async () => {
    await Promise.allSettled(Array.from(inFlightRef.current.values()))
  }, [])

  /**
   * Hands the uploaded attachments to a message and empties the list. Images picked in this tab get an object
   * URL so the bubble can show a thumbnail; the URL is released when the hook unmounts.
   */
  const takeReady = useCallback((): MessageAttachment[] => {
    const ready = attachmentsRef.current
      .filter((entry): entry is PendingAttachment & { attachment: AssistantAttachment } => Boolean(entry.attachment))
      .slice(0, ATTACHMENT_MAX_COUNT)
      .map((entry) => {
        const previewUrl =
          isImageAttachment(entry.attachment.contentType) && typeof URL.createObjectURL === 'function'
            ? URL.createObjectURL(entry.file)
            : undefined
        if (previewUrl) previewUrlsRef.current.set(entry.attachment.attachmentId, previewUrl)
        return { ...entry.attachment, previewUrl }
      })
    replace([])
    return ready
  }, [replace])

  useEffect(
    () => () => {
      abortControllersRef.current.forEach((controller) => controller.abort())
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    },
    []
  )

  // Uploads and ready ids belong to the org they were created under; drop them on switch so the next
  // send cannot attach another tenant's files (or finish an in-flight upload against the old org).
  useEffect(() => {
    if (lastOrgIdRef.current === organizationId) return
    lastOrgIdRef.current = organizationId
    clear()
  }, [clear, organizationId])

  const isUploading = useMemo(() => attachments.some((entry) => entry.status === 'uploading'), [attachments])
  const readyCount = useMemo(() => attachments.filter((entry) => entry.status === 'ready').length, [attachments])

  return { attachments, addFiles, remove, clear, waitForUploads, takeReady, isUploading, readyCount }
}
